import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "@/components/data-tables/data-table-column-header";
import { formatDate } from "@/lib/utils";
import { ProviderActions } from "../ProviderActions";
import { Provider } from "@shared/validators/admin/settings-validators";
import { embeddingProviderMap, EmbeddingProvider } from "@shared/types/providers/embedding-provider";
import i18n from "@/utils/i18n/i18n";

interface EmbeddingProviderActions {
    onViewDetails?: (provider: Provider) => void;
    onEdit?: (provider: Provider) => void;
    onToggleEnabled?: (provider: Provider) => void;
}

export function createEmbeddingProviderColumns(actions: EmbeddingProviderActions): ColumnDef<Provider>[] {
    return [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label={i18n.t("columns.selectAll")}
                    className="translate-y-[2px]"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label={i18n.t("columns.selectRow")}
                    className="translate-y-[2px]"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "display_name",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.displayName")} />,
            cell: ({ row }) => {
                return (
                    <div className="flex space-x-2">
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {row.getValue("display_name")}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "provider",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.provider")} />,
            cell: ({ row }) => {
                const providerValue = row.getValue("provider") as EmbeddingProvider;
                const providerInfo = embeddingProviderMap[providerValue];
                return (
                    <div className="flex items-center gap-2">
                        {providerInfo?.image && (
                            <img 
                                src={providerInfo.image} 
                                alt={providerInfo.label} 
                                className="h-5 w-5 object-contain" 
                            />
                        )}
                        <span className="max-w-32 truncate sm:max-w-72 md:max-w-[31rem]">
                            {providerInfo?.label || providerValue}
                        </span>
                    </div>
                );
            },
        },
        {
            accessorKey: "enabled",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.status")} />,
            cell: ({ row }) => {
                const enabled = row.getValue("enabled") as boolean;
                return <Badge variant={enabled ? "default" : "secondary"}>{enabled ? i18n.t("columns.enabled") : i18n.t("columns.disabled")}</Badge>;
            },
            filterFn: (row, id, value) => {
                return value.includes(row.getValue(id));
            },
        },
        {
            accessorKey: "created_at",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.created")} />,
            cell: ({ row }) => formatDate(row.getValue("created_at")),
        },
        {
            id: "actions",
            header: ({ column }) => <DataTableColumnHeader column={column} title={i18n.t("columns.actions")} className="text-end" />,
            cell: ({ row }) => (
                <div className="flex justify-end">
                    <ProviderActions
                        provider={row.original}
                        onEdit={actions.onEdit}
                        onToggleEnabled={actions.onToggleEnabled}
                    />
                </div>
            ),
        },
    ];
}

export const EmbeddingProviderSearchableColumns = [
    {
        id: "display_name" as keyof Provider,
        get title() { return i18n.t("columns.name"); },
    },
];
